import { GridRenderCellParams } from "@mui/x-data-grid";
import { messages } from "../../../locales/messages";
import { AllowedTableColumn } from "../../../utils/types/AllowTableTypes";
import { RenderEditButton } from "../../components/RenderEditButtonTable";
import { useTranslation } from "react-i18next";
import {
  GfImage,
  GfImageDto,
  GfImageTag,
} from "@swagger/typescript-fetch-goflyer";
import { useNavigate } from "react-router-dom";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";
import { RenderDeleteButton } from "../../components/RenderDeleteButtonTable";
import { RenderMultiImageTable } from "../../components/RenderImageTable";
import { IconButton, Tooltip } from "@mui/material";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import { routes } from "../../../utils/constant/navigationRoutes";

interface ImagesTableColumnsProps {
  handleOnDelete: (value: GfImage) => void;
  handleOnEditTags: (value: GfImage) => void;
}

export const useImagesTableColumns = ({
  handleOnDelete,
  handleOnEditTags,
}: ImagesTableColumnsProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const values: AllowedTableColumn[] = ["updatedAt", "createdAt"];

  const handleOnClickEdit = async (value: GfImage) => {
    navigate(routes.image.editById(value?.id || ""));
  };

  const columns = [
    {
      field: "edit",
      width: 90,
      headerName: `${t(messages.Edit())}`,
      filterable: false,
      renderCell: (props: GridRenderCellParams) => (
        <PermissionComponentWrapper
          requiredPermission={["edit"]}
          requiredPermissionGroup={["image"]}
        >
          <RenderEditButton onClick={handleOnClickEdit} {...props} />
        </PermissionComponentWrapper>
      ),
      sortable: false,
    },
    {
      field: "src",
      width: 100,
      headerName: `${t(messages["source"]())}`,
      filterable: false,
      renderCell: (props: GridRenderCellParams<GfImageDto>) => {
        return (
          <RenderMultiImageTable images={[props.row]}></RenderMultiImageTable>
        );
      },
      sortable: false,
    },
    {
      field: "srcSmall",
      width: 100,
      headerName: `${t(messages["sourceSmall"]())}`,
      filterable: false,
      renderCell: (props: GridRenderCellParams<GfImageDto>) => {
        return (
          props.row.srcSmall && (
            <RenderMultiImageTable
              images={[{ ...props.row, src: props.row.srcSmall }]}
            ></RenderMultiImageTable>
          )
        );
      },
      sortable: false,
    },
    {
      field: "srcTinyPng",
      width: 100,
      headerName: `${t(messages["sourceTinyPng"]())}`,
      filterable: false,
      renderCell: (props: GridRenderCellParams<GfImageDto>) => {
        return (
          props.row.srcTinyPng && (
            <RenderMultiImageTable
              images={[{ ...props.row, src: props.row.srcTinyPng }]}
            ></RenderMultiImageTable>
          )
        );
      },
      sortable: false,
    },
    {
      field: "alt",
      width: 300,
      headerName: `${t(messages["alt"]())}`,
      valueGetter: (params: GridRenderCellParams) => params.row.alt,
    },
    {
      field: "imageTags",
      width: 300,
      headerName: `${t(messages.ImageTag())}`,
      renderCell: (params: GridRenderCellParams<GfImage>) => {
        const tags =
          params.row.imageTags?.map((val: GfImageTag) => val.name).join(", ") ||
          "";
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              width: "100%",
            }}
          >
            <span
              style={{
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {tags}
            </span>
            <PermissionComponentWrapper
              requiredPermission={["edit"]}
              requiredPermissionGroup={["image"]}
            >
              <Tooltip title={t(messages.Edit())}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOnEditTags(params.row as GfImage);
                  }}
                >
                  <LocalOfferIcon sx={{ fontSize: 16, color: "#ff8038" }} />
                </IconButton>
              </Tooltip>
            </PermissionComponentWrapper>
          </div>
        );
      },
      sortable: false,
    },
    {
      field: "s3Size",
      width: 150,
      headerName: `${t(messages["s3Size"]())}`,
      valueGetter: (params: GridRenderCellParams) => {
        const bytes = params.row.s3Size || 0;
        if (bytes === 0 || !bytes) return "-";
        if (bytes < 1024) return `${bytes} Bytes`;
        if (bytes < 1048576) return `${(bytes / 1024).toFixed(2)} KB`;
        return `${(bytes / 1048576).toFixed(2)} MB`;
      },
    },

    {
      field: "mediaType",
      width: 150,
      headerName: `${t(messages["mediaType"]())}`,
      valueGetter: (params: GridRenderCellParams) => params.row.mediaType,
    },
    {
      field: "height",
      type: "number",
      width: 100,
      headerName: `${t(messages["height"]())}`,
      valueGetter: (params: GridRenderCellParams) => params.row.height,
    },
    {
      field: "width",
      type: "number",
      width: 100,
      headerName: `${t(messages["width"]())}`,
      valueGetter: (params: GridRenderCellParams) => params.row.width,
    },

    ...values.map((value) => {
      return {
        width: 200,
        field: value,
        headerName: `${t(messages[value]())}`,
        type: "dateTime",
        valueGetter: (val: GridRenderCellParams) => {
          return val && new Date(val?.row[value]);
        },
      };
    }),

    {
      field: "id",
      width: 300,
      headerName: `${t(messages["id"]())}`,
      filterable: false,
    },
    {
      field: "delete",
      width: 90,
      headerName: `${t(messages.Delete())}`,
      filterable: false,
      renderCell: (props: GridRenderCellParams) => (
        <PermissionComponentWrapper
          requiredPermission={["delete"]}
          requiredPermissionGroup={["image"]}
        >
          <RenderDeleteButton
            onClick={() => handleOnDelete(props.row as GfImage)}
            {...props}
          />
        </PermissionComponentWrapper>
      ),
      sortable: false,
    },
  ];

  return columns;
};
